import './cookie-consent.pcss';

type Options = Partial<CookieConsentOptions>;

export interface CookieConsentElement extends HTMLElement {
  cookieConsent?: CookieConsent;
}

export interface CookieConsentOptions {
  acceptAllSelector: string;
  acceptNecessarySelector: string;
  activeClass: string;
}

export class CookieConsent {
  static defaults = {
    acceptAllSelector: '[data-cookie-consent-accept-all]',
    acceptNecessarySelector: '[data-cookie-consent-accept-necessary]',
    activeClass: 'is-active',
  };

  acceptAllElement: HTMLElement;
  acceptNecessaryElement: HTMLElement;
  element: CookieConsentElement;
  options: CookieConsentOptions;

  constructor(element: CookieConsentElement, options?: Options) {
    if (!element || element.cookieConsent) {
      return;
    }

    element.cookieConsent = this;
    this.element = element;

    this.options = {
      ...CookieConsent.defaults,
      ...this.element.dataset,
      ...options,
    };

    const acceptAllElement = this.element.querySelector(
      this.options.acceptAllSelector,
    ) as HTMLElement;
    const acceptNecessaryElement = this.element.querySelector(
      this.options.acceptNecessarySelector,
    ) as HTMLElement;

    if (!acceptAllElement || !acceptNecessaryElement) {
      this.destroy();
      return;
    }

    this.acceptAllElement = acceptAllElement;
    this.acceptNecessaryElement = acceptNecessaryElement;

    this.init();
  }

  init() {
    window.Shopify.loadFeatures(
      [
        {
          name: 'consent-tracking-api',
          version: '0.1',
        },
      ],
      (error: any) => {
        if (error) {
          this.destroy();
          return;
        }

        if (
          window.Shopify.customerPrivacy &&
          window.Shopify.customerPrivacy.shouldShowGDPRBanner()
        ) {
          this.setActive();
          this.bind();
        }
      },
    );
  }

  bind() {
    this.acceptAllElement.addEventListener('click', this.acceptAll);
    this.acceptNecessaryElement.addEventListener('click', this.acceptNecessary);
    document.addEventListener('cookies:accept_all', this.handleAccept);
    document.addEventListener('cookies:accept_necessary', this.handleAccept);
  }

  setActive(active: boolean = true) {
    this.element.classList.toggle(this.options.activeClass, active);
  }

  acceptAll() {
    window.Shopify.customerPrivacy.setTrackingConsent(true, () => {
      document.dispatchEvent(new Event('cookies:accept_all'));
    });
  }

  acceptNecessary() {
    window.Shopify.customerPrivacy.setTrackingConsent(false, () => {
      document.dispatchEvent(new Event('cookies:accept_necessary'));
    });
  }

  handleAccept = () => {
    this.setActive(false);
  };

  destroy() {
    this.setActive(false);
    this.acceptAllElement.removeEventListener('click', this.acceptAll);
    this.acceptNecessaryElement.removeEventListener(
      'click',
      this.acceptNecessary,
    );
    document.removeEventListener('cookies:accept_all', this.handleAccept);
    document.removeEventListener('cookies:accept_necessary', this.handleAccept);
    this.element.cookieConsent = undefined;
  }
}
